/**
 * EssaySlotService
 * 
 * Core service for managing essay slots - the organized tracking system
 * for all essay requirements across colleges. Provides automatic slot generation,
 * content assignment, timeline synchronization, and intelligent reuse suggestions.
 */

import { supabase } from '../lib/supabase.js';
import StatusMappingService from './StatusMappingService.js';
import DateSynchronizationService from './DateSynchronizationService.js';

class EssaySlotService {
  /**
   * Generate slots for a college based on its essay requirements
   * Called automatically when a college is added (via SQL trigger)
   * This method is for manual generation or regeneration if needed
   */
  static async generateSlotsForCollege(userId, collegeId) {
    try {
      console.log(`[EssaySlotService] 🎯 Starting generateSlotsForCollege for userId: ${userId}, collegeId: ${collegeId}`);

      // Get college with essay requirements and deadline info from global_colleges
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          *,
          global_colleges(
            ed_deadline,
            ea_deadline,
            rd_deadline,
            num_essays,
            essay_prompts,
            common_app_accepted: app_platforms
          )
        `)
        .eq('id', collegeId)
        .single();

      if (collegeError) {
        console.error('[EssaySlotService] ❌ Error fetching college:', collegeError);
        throw collegeError;
      }
      if (!college) {
        console.error('[EssaySlotService] ❌ College not found');
        throw new Error('College not found');
      }

      console.log('[EssaySlotService] 📊 College data retrieved:', college);
      console.log('[EssaySlotService] 📊 essay_requirements:', college.essay_requirements);
      console.log('[EssaySlotService] 📊 essay_prompts:', college.global_colleges?.essay_prompts);

      // Check both essay_requirements and essay_prompts (newer format from AI fetching)
      // Use essay_requirements if it has an essays array, otherwise use essay_prompts
      const hasEssayRequirements = college.essay_requirements?.essays && Array.isArray(college.essay_requirements.essays) && college.essay_requirements.essays.length > 0;
      const requirements = hasEssayRequirements ? college.essay_requirements : (college.global_colleges?.essay_prompts || {});

      console.log('[EssaySlotService] 📋 Using requirements:', requirements);
      console.log('[EssaySlotService] 📋 Requirements.essays:', requirements.essays);

      const slots = [];

      // Create personal statement slot if Common App accepted or if explicitly required
      const needsPersonalStatement = requirements.common_app_accepted ||
                                      requirements.personalStatementRequired ||
                                      requirements.platform === 'common_app';
      if (needsPersonalStatement) {
        const personalSlot = await this.createOrGetPersonalStatementSlot(userId);
        if (personalSlot) slots.push(personalSlot);
      }

      // Create slots for each essay requirement (with duplicate checking)
      if (requirements.essays && Array.isArray(requirements.essays)) {
        console.log(`[EssaySlotService] 📝 Found ${requirements.essays.length} essays to create slots for`);

        // Check for existing slots for this college to prevent duplicates
        const { data: existingSlots } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('user_id', userId)
          .eq('college_id', collegeId);

        console.log(`[EssaySlotService] 📋 Found ${existingSlots?.length || 0} existing slots`);

        const existingSlotsByPosition = (existingSlots || []).reduce((acc, slot) => {
          acc[slot.essay_slot] = slot;
          return acc;
        }, {});

        for (let i = 0; i < requirements.essays.length; i++) {
          const essay = requirements.essays[i];
          const slotPosition = i + 1;

          console.log(`[EssaySlotService] 🔨 Processing essay ${slotPosition}:`, essay);
          console.log(`[EssaySlotService] 🔍 Essay has options?`, essay.options);
          console.log(`[EssaySlotService] 🔍 Is array?`, Array.isArray(essay.options));
          console.log(`[EssaySlotService] 🔍 Options length:`, essay.options?.length);

          // Check if this is a choice group (has options array)
          if (essay.options && Array.isArray(essay.options) && essay.options.length > 0) {
            console.log(`[EssaySlotService] 🎯 Detected choice group with ${essay.options.length} options`);

            // Generate slots for choice group
            const groupSlots = await this.createChoiceGroupSlots({
              userId,
              collegeId,
              college,
              essay,
              baseSlotPosition: slotPosition,
              existingSlots: existingSlotsByPosition
            });

            slots.push(...groupSlots);
            continue;
          }

          // Regular slot handling (no choice group)
          // Skip if slot already exists for this position
          if (existingSlotsByPosition[slotPosition]) {
            console.log(`[EssaySlotService] ⏭️ Essay slot ${slotPosition} already exists for college ${collegeId}`);
            const existingSlot = existingSlotsByPosition[slotPosition];
            slots.push(existingSlot);

            // Check if timeline item exists for this slot, create if missing
            if (!existingSlot.timeline_item_id) {
              console.log(`[EssaySlotService] 📅 Creating missing timeline item for slot ${slotPosition}`);
              await this.createTimelineItemForSlot(existingSlot, college);
            }
            continue;
          }

          console.log(`[EssaySlotService] ➕ Creating new slot for position ${slotPosition}`);
          const slot = await this.createSlot({
            user_id: userId,
            college_id: collegeId,
            slot_type: essay.type || 'supplemental',
            essay_slot: slotPosition,
            prompt_text: essay.prompt,
            word_limit: essay.word_limit || essay.wordLimit,
            is_optional: !essay.required
            // Note: prompt_choice_type will be added after migration is run
          });

          if (slot) {
            console.log(`[EssaySlotService] ✅ Created slot ${slotPosition}:`, slot);
            slots.push(slot);
            // Create corresponding timeline item
            await this.createTimelineItemForSlot(slot, college);
          } else {
            console.error(`[EssaySlotService] ❌ Failed to create slot ${slotPosition}`);
          }
        }
      } else {
        console.log('[EssaySlotService] ⚠️ No essays array found in requirements or array is empty');
      }

      console.log(`[EssaySlotService] ✅ Finished generating slots. Total created: ${slots.length}`);
      return slots;
    } catch (error) {
      console.error('[EssaySlotService] ❌ Error generating slots for college:', error);
      console.error('[EssaySlotService] Stack:', error.stack);
      throw error;
    }
  }

  /**
   * Create slots for a prompt choice group
   * Each option gets its own slot, all sharing one timeline item
   */
  static async createChoiceGroupSlots({ userId, collegeId, college, essay, baseSlotPosition, existingSlots }) {
    try {
      const groupId = this.generateUUID();
      const choiceType = essay.choice_type || 'pick_one';
      const groupLabel = essay.group_label || `Essay Choice ${baseSlotPosition}`;

      console.log(`[EssaySlotService] 🎯 Creating choice group: ${groupLabel} (${choiceType})`);

      // Create ONE shared timeline item for the entire group
      const timelineItem = await this.createTimelineItemForGroup({
        userId,
        collegeId,
        college,
        groupLabel,
        choiceType,
        essay
      });

      if (!timelineItem) {
        console.error('[EssaySlotService] ❌ Failed to create timeline item for choice group');
        return [];
      }

      console.log(`[EssaySlotService] ✅ Created shared timeline item:`, timelineItem.id);

      // Create one slot per option
      const createdSlots = [];
      for (let optionIdx = 0; optionIdx < essay.options.length; optionIdx++) {
        const option = essay.options[optionIdx];
        const slotPosition = baseSlotPosition + optionIdx;

        // Skip if slot already exists
        if (existingSlots[slotPosition]) {
          console.log(`[EssaySlotService] ⏭️ Slot ${slotPosition} already exists, skipping`);
          createdSlots.push(existingSlots[slotPosition]);
          continue;
        }

        const slot = await this.createSlot({
          user_id: userId,
          college_id: collegeId,
          prompt_group_id: groupId,
          group_label: groupLabel,
          timeline_item_id: timelineItem.id, // SHARED timeline item
          slot_type: essay.type || 'supplemental',
          essay_slot: slotPosition,
          prompt_text: option.prompt,
          word_limit: option.word_limit || option.wordLimit || essay.word_limit || essay.wordLimit, // Handle both camelCase and snake_case
          is_optional: essay.is_optional || false,
          prompt_choice_type: choiceType,
          selection_status: 'exploratory', // Default status
          prompt_option_id: option.option_id || String.fromCharCode(65 + optionIdx) // A, B, C...
        });

        if (slot) {
          console.log(`[EssaySlotService] ✅ Created choice slot ${optionIdx + 1}/${essay.options.length}:`, slot.id);
          createdSlots.push(slot);
        } else {
          console.error(`[EssaySlotService] ❌ Failed to create choice slot ${optionIdx + 1}`);
        }
      }

      console.log(`[EssaySlotService] ✅ Created ${createdSlots.length} slots for choice group`);
      return createdSlots;
    } catch (error) {
      console.error('[EssaySlotService] ❌ Error creating choice group slots:', error);
      return [];
    }
  }

  /**
   * Create timeline item for a choice group
   */
  static async createTimelineItemForGroup({ userId, collegeId, college, groupLabel, choiceType, essay }) {
    try {
      const deadline = this.calculateSlotDeadline(college);

      const timelineItem = {
        user_id: userId,
        title: groupLabel,
        group_label: groupLabel,
        description: essay.prompt || `Choose one essay prompt and complete it for ${college.name}`,
        category: 'essay',
        epic_id: 'essay',
        college_id: collegeId,
        college_name: college.name,
        priority: essay.required ? 'high' : 'medium',
        workflow_phase: 'ready',
        completion_status: 'not_started',
        due_date: deadline,
        scheduled_date: deadline,
        source_type: 'essay_slot',
        source_id: `essay_choice_group_${collegeId}_${Date.now()}`,
        represents_choice_group: true,
        choice_group_metadata: {
          choice_type: choiceType,
          total_options: essay.options?.length || 0,
          created_at: new Date().toISOString()
        },
        estimated_duration: this.getEssayEstimatedDuration({
          slot_type: essay.type || 'supplemental',
          word_limit: essay.word_limit || essay.wordLimit || 250
        }),
        module_name: 'Essay Builder',
        module_link: this.getEssayModuleLink(essay.type || 'supplemental'),
        resource_urls: this.getEssayResourceUrls(essay.type || 'supplemental'),
        metadata: {
          college_id: collegeId,
          college_name: college.name,
          word_limit: essay.word_limit || essay.wordLimit,
          is_choice_group: true
        }
      };

      const { data, error } = await supabase
        .from('timeline_items')
        .insert(timelineItem)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('[EssaySlotService] Error creating timeline item for group:', error);
      return null;
    }
  }

  /**
   * Finalize a prompt choice (for pick_one groups, unfinalizes others)
   */
  static async finalizePromptChoice(slotId) {
    try {
      // Get the slot being finalized
      const { data: slot, error: slotError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('id', slotId)
        .single();

      if (slotError) throw slotError;
      if (!slot) throw new Error('Slot not found');
      if (!slot.prompt_group_id) throw new Error('Slot is not part of a choice group');

      console.log(`[EssaySlotService] 🎯 Finalizing prompt choice for slot ${slotId}`);

      // Get all slots in the group
      const groupSlots = await this.getSlotsByGroupId(slot.prompt_group_id);

      // For pick_one: unfinalize all others
      if (slot.prompt_choice_type === 'pick_one') {
        await Promise.all(
          groupSlots
            .filter(s => s.id !== slotId)
            .map(s =>
              supabase
                .from('essay_slots')
                .update({ selection_status: 'exploratory' })
                .eq('id', s.id)
            )
        );
      }

      // Finalize the selected slot
      await supabase
        .from('essay_slots')
        .update({ selection_status: 'finalized' })
        .eq('id', slotId);

      // Update timeline metadata
      const finalizedSlotIds = groupSlots
        .filter(s => s.id === slotId || (s.selection_status === 'finalized' && s.id !== slotId))
        .map(s => s.id);

      await supabase
        .from('timeline_items')
        .update({
          choice_group_metadata: {
            ...slot.choice_group_metadata,
            finalized_slot_ids: finalizedSlotIds,
            last_selection_at: new Date().toISOString()
          }
        })
        .eq('id', slot.timeline_item_id);

      console.log(`[EssaySlotService] ✅ Finalized prompt choice`);
      return { success: true, slot_id: slotId };
    } catch (error) {
      console.error('[EssaySlotService] Error finalizing prompt choice:', error);
      throw error;
    }
  }

  /**
   * Update slot selection status (exploratory, finalized, rejected)
   */
  static async updateSlotSelectionStatus(slotId, newStatus) {
    try {
      const { data: slot } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('id', slotId)
        .single();

      if (!slot) throw new Error('Slot not found');

      // If finalizing a pick_one slot, use finalizePromptChoice
      if (newStatus === 'finalized' && slot.prompt_choice_type === 'pick_one') {
        return this.finalizePromptChoice(slotId);
      }

      // For other status changes or optional_additional
      await supabase
        .from('essay_slots')
        .update({ selection_status: newStatus })
        .eq('id', slotId);

      console.log(`[EssaySlotService] ✅ Updated selection status to ${newStatus}`);
      return { success: true, slot_id: slotId, status: newStatus };
    } catch (error) {
      console.error('[EssaySlotService] Error updating selection status:', error);
      throw error;
    }
  }

  /**
   * Validate prompt group completion
   */
  static async validatePromptGroupCompletion(promptGroupId) {
    try {
      const slots = await this.getSlotsByGroupId(promptGroupId);

      if (!slots || slots.length === 0) {
        return { valid: false, message: 'No slots found for this group' };
      }

      const choiceType = slots[0].prompt_choice_type;
      const finalizedCount = slots.filter(s => s.selection_status === 'finalized').length;

      if (choiceType === 'pick_one') {
        if (finalizedCount === 0) {
          return { valid: false, message: 'Please select one prompt to finalize' };
        }
        if (finalizedCount > 1) {
          return { valid: false, message: 'Only one prompt can be finalized' };
        }
        return { valid: true, message: 'Valid' };
      }

      // optional_additional: any count is fine
      return { valid: true, message: 'Valid' };
    } catch (error) {
      console.error('[EssaySlotService] Error validating prompt group:', error);
      return { valid: false, message: 'Error validating group' };
    }
  }

  /**
   * Get all slots in a prompt group
   */
  static async getSlotsByGroupId(promptGroupId) {
    try {
      const { data, error } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('prompt_group_id', promptGroupId)
        .order('essay_slot');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('[EssaySlotService] Error getting slots by group ID:', error);
      return [];
    }
  }

  /**
   * Generate a UUID (helper method)
   */
  static generateUUID() {
    return crypto.randomUUID();
  }

  /**
   * Create or get the personal statement slot (shared across all Common App schools)
   */
  static async createOrGetPersonalStatementSlot(userId) {
    try {
      // Check if personal statement slot already exists
      const { data: existingSlot } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('user_id', userId)
        .is('college_id', null)
        .eq('slot_type', 'personal_statement')
        .single();

      if (existingSlot) return existingSlot;

      // Create new personal statement slot
      const { data: newSlot, error } = await supabase
        .from('essay_slots')
        .insert({
          user_id: userId,
          college_id: null,
          slot_type: 'personal_statement',
          essay_slot: 1,
          prompt_text: 'Personal Statement / Common App Essay - Choose from one of the Common App prompts',
          word_limit: 650,
          is_optional: false,
          status: 'empty'
        })
        .select()
        .single();

      if (error) throw error;

      // Create a master timeline item for personal statement
      await this.createPersonalStatementTimelineItem(userId, newSlot.id);

      return newSlot;
    } catch (error) {
      console.error('Error creating personal statement slot:', error);
      return null;
    }
  }

  /**
   * Create a single slot
   */
  static async createSlot(slotData) {
    try {
      console.log('[EssaySlotService] 💾 Attempting to insert slot data:', slotData);

      const { data, error } = await supabase
        .from('essay_slots')
        .insert(slotData)
        .select()
        .single();

      if (error) {
        console.error('[EssaySlotService] ❌ Insert error:', error);
        throw error;
      }

      console.log('[EssaySlotService] ✅ Slot created successfully:', data);
      return data;
    } catch (error) {
      console.error('[EssaySlotService] Error creating slot:', error);
      return null;
    }
  }

  /**
   * Create timeline item for a slot
   */
  static async createTimelineItemForSlot(slot, college) {
    try {
      // Determine deadline based on college application deadlines and chosen round
      const deadline = this.calculateSlotDeadline(college);
      
      const timelineItem = {
        user_id: slot.user_id,
        title: this.getSlotTypeLabel(slot.slot_type),
        description: slot.prompt_text || `Complete ${slot.slot_type} essay for ${college.name}`,
        category: 'essay',
        epic_id: 'essay',
        college_id: slot.college_id,
        college_name: college.name,
        priority: slot.is_optional ? 'medium' : 'high',
        workflow_phase: 'ready',
        completion_status: 'not_started', 
        due_date: deadline,
        scheduled_date: deadline,
        // Set source_type so it appears in milestone widgets
        source_type: 'essay_slot',
        source_id: `essay_slot_${slot.id}`,
        // Add enhanced fields for better UX
        estimated_duration: this.getEssayEstimatedDuration(slot),
        module_name: 'Essay Builder',
        module_link: this.getEssayModuleLink(slot.slot_type),
        resource_urls: this.getEssayResourceUrls(slot.slot_type),
        metadata: {
          slot_id: slot.id,
          college_id: college.id,
          college_name: college.name,
          word_limit: slot.word_limit,
          is_optional: slot.is_optional
        }
      };

      const { data, error } = await supabase
        .from('timeline_items')
        .insert(timelineItem)
        .select()
        .single();

      if (error) throw error;

      // Update slot with timeline_item_id
      await supabase
        .from('essay_slots')
        .update({ timeline_item_id: data.id })
        .eq('id', slot.id);

      return data;
    } catch (error) {
      console.error('Error creating timeline item for slot:', error);
      return null;
    }
  }

  /**
   * Create master timeline item for personal statement
   */
  static async createPersonalStatementTimelineItem(userId, slotId) {
    try {
      // Set deadline 2 weeks before earliest application deadline
      // Get colleges with their global_colleges deadline info
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select(`
          id,
          global_colleges(
            ed_deadline,
            ea_deadline,
            rd_deadline
          )
        `)
        .eq('user_id', userId);

      let earliestDeadline = null;
      if (colleges && colleges.length > 0) {
        const allDeadlines = colleges.flatMap(c => 
          [
            c.global_colleges?.ed_deadline,
            c.global_colleges?.ea_deadline,
            c.global_colleges?.rd_deadline
          ].filter(Boolean)
        );
        earliestDeadline = allDeadlines.sort()[0];
      }

      const deadline = earliestDeadline ? 
        new Date(new Date(earliestDeadline).getTime() - 14 * 24 * 60 * 60 * 1000) :
        new Date(new Date().getFullYear(), 11, 1); // Default to Dec 1

      const { data, error } = await supabase
        .from('timeline_items')
        .insert({
          user_id: userId,
          title: 'Personal Statement / Common App Essay',
          description: 'Complete your main college essay (650 words)',
          category: 'essay',
          epic_id: 'essay',
          priority: 'high',
          workflow_phase: 'ready',
          completion_status: 'not_started',
          due_date: deadline.toISOString(),
          scheduled_date: deadline.toISOString(),
          // Set source_type so it appears in milestone widgets
          source_type: 'essay_slot',
          source_id: `essay_slot_${slotId}`,
          // Add enhanced fields
          estimated_duration: 240, // 4 hours for personal statement
          module_name: 'Essay Builder',
          module_link: '/college/essays?tab=free-write',
          resource_urls: [
            'https://www.commonapp.org/apply/essay-prompts',
            'https://www.collegeessayguy.com/personal-statement'
          ],
          metadata: {
            slot_id: slotId,
            is_personal_statement: true
          }
        })
        .select()
        .single();

      if (error) throw error;

      // Update slot with timeline_item_id
      await supabase
        .from('essay_slots')
        .update({ timeline_item_id: data.id })
        .eq('id', slotId);

      return data;
    } catch (error) {
      console.error('Error creating personal statement timeline item:', error);
      return null;
    }
  }

  /**
   * Assign content to a slot (legacy method kept for compatibility)
   */
  static async assignContentToSlot(slotId, contentType, contentId, isPrimary = true) {
    // Redirect to new method
    return this.assignContent(slotId, contentType, contentId);
  }

  /**
   * Assign content to a slot with enhanced metadata tracking
   */
  static async assignContent(slotId, contentType, contentId) {
    try {
      // Get current slot data
      const { data: currentSlot, error: fetchError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('id', slotId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentSlot) throw new Error('Slot not found');

      const updateData = {};
      const metadata = currentSlot.attached_metadata || {};

      switch (contentType) {
        case 'essay':
          updateData.assigned_essay_id = contentId;
          updateData.status = 'drafting';
          // Track in metadata
          metadata[contentId] = {
            attached_at: new Date().toISOString(),
            type: 'essay',
            is_primary: true
          };
          updateData.attached_metadata = metadata;
          break;
          
        case 'freewrite':
          // Add to array of freewrites
          const freewriteIds = currentSlot.assigned_freewrite_ids || [];
          if (!freewriteIds.includes(contentId)) {
            freewriteIds.push(contentId);
            updateData.assigned_freewrite_ids = freewriteIds;
            updateData.status = currentSlot.assigned_essay_id ? 'drafting' : 'brainstorming';
            // Track in metadata
            metadata[contentId] = {
              attached_at: new Date().toISOString(),
              type: 'freewrite'
            };
            updateData.attached_metadata = metadata;
          }
          break;
          
        case 'idea':
          // Add to array of ideas
          const ideaIds = currentSlot.assigned_idea_ids || [];
          if (!ideaIds.includes(contentId)) {
            ideaIds.push(contentId);
            updateData.assigned_idea_ids = ideaIds;
            updateData.status = currentSlot.assigned_essay_id ? 'drafting' : 'brainstorming';
            // Track in metadata
            metadata[contentId] = {
              attached_at: new Date().toISOString(),
              type: 'idea'
            };
            updateData.attached_metadata = metadata;
          }
          break;
      }

      const { data, error } = await supabase
        .from('essay_slots')
        .update(updateData)
        .eq('id', slotId)
        .select()
        .single();

      if (error) throw error;

      // Sync status with timeline
      await this.syncSlotWithTimeline(slotId);

      return data;
    } catch (error) {
      console.error('Error assigning content to slot:', error);
      throw error;
    }
  }

  /**
   * Update slot with partial data
   */
  static async updateSlot(slotId, updateData) {
    try {
      const { data, error } = await supabase
        .from('essay_slots')
        .update(updateData)
        .eq('id', slotId)
        .select()
        .single();

      if (error) throw error;

      // Sync status with timeline if status was updated
      if (updateData.status) {
        await this.syncSlotWithTimeline(slotId);
      }

      return data;
    } catch (error) {
      console.error('Error updating slot:', error);
      throw error;
    }
  }

  /**
   * Remove content from a slot
   */
  static async removeContentFromSlot(slotId, contentType, contentId) {
    try {
      const { data: slot } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('id', slotId)
        .single();

      if (!slot) throw new Error('Slot not found');

      const updateData = {};

      switch (contentType) {
        case 'essay':
          if (slot.assigned_essay_id === contentId) {
            updateData.assigned_essay_id = null;
            updateData.status = 'empty';
          }
          break;
        case 'freewrite':
          updateData.assigned_freewrite_ids = 
            (slot.assigned_freewrite_ids || []).filter(id => id !== contentId);
          break;
        case 'idea':
          updateData.assigned_idea_ids = 
            (slot.assigned_idea_ids || []).filter(id => id !== contentId);
          break;
      }

      const { data, error } = await supabase
        .from('essay_slots')
        .update(updateData)
        .eq('id', slotId)
        .select()
        .single();

      if (error) throw error;

      // Sync status with timeline
      await this.syncSlotWithTimeline(slotId);

      return data;
    } catch (error) {
      console.error('Error removing content from slot:', error);
      throw error;
    }
  }

  /**
   * Update slot status and sync with timeline
   */
  static async updateSlotStatus(slotId, status, completionPercentage = null) {
    try {
      const updateData = { status };
      if (completionPercentage !== null) {
        updateData.completion_percentage = completionPercentage;
      }

      const { data, error } = await supabase
        .from('essay_slots')
        .update(updateData)
        .eq('id', slotId)
        .select()
        .single();

      if (error) throw error;

      // Sync with timeline
      await this.syncSlotWithTimeline(slotId);

      return data;
    } catch (error) {
      console.error('Error updating slot status:', error);
      throw error;
    }
  }

  /**
   * Sync slot status with timeline item
   */
  static async syncSlotWithTimeline(slotId) {
    try {
      const { data: slot } = await supabase
        .from('essay_slots')
        .select('*, timeline_items(*)')
        .eq('id', slotId)
        .single();

      if (!slot || !slot.timeline_item_id) return;

      // Map slot status to timeline status
      const timelineStatus = this.mapSlotStatusToTimeline(slot.status);

      await supabase
        .from('timeline_items')
        .update({
          status: timelineStatus,
          progress_percentage: slot.completion_percentage
        })
        .eq('id', slot.timeline_item_id);

    } catch (error) {
      console.error('Error syncing slot with timeline:', error);
    }
  }

  /**
   * Add strategy note to essay slot (for Reach School positioning, etc.)
   * @param {string} collegeId - The college ID
   * @param {string} userId - The user ID
   * @param {string} noteType - Type of note (e.g., 'specific_positioning', 'why_us_angles')
   * @param {object} content - The note content (action, why, tactic, etc.)
   */
  static async addStrategyNote(collegeId, userId, noteType, content) {
    try {
      // Find all essay slots for this college (specifically looking for 'why_us' type)
      const { data: slots, error: fetchError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('user_id', userId)
        .eq('college_id', collegeId)
        .or('slot_type.eq.why_us,slot_type.eq.supplemental');

      if (fetchError) throw fetchError;

      if (!slots || slots.length === 0) {
        console.log('No essay slots found for college - strategy note not saved');
        return null;
      }

      // Add note to all relevant slots (typically why_us essays benefit most from positioning)
      const updates = [];
      for (const slot of slots) {
        const currentInsights = slot.college_insights || {};
        const currentNotes = currentInsights.strategy_notes || {};

        // Add or update the specific note type
        const updatedNotes = {
          ...currentNotes,
          [noteType]: {
            value: content.action || content,
            details: {
              why: content.why,
              tactic: content.tactic,
              addedAt: new Date().toISOString()
            }
          }
        };

        const { error: updateError } = await supabase
          .from('essay_slots')
          .update({
            college_insights: {
              ...currentInsights,
              strategy_notes: updatedNotes
            }
          })
          .eq('id', slot.id);

        if (updateError) {
          console.error('Error updating slot:', updateError);
        } else {
          updates.push(slot.id);
        }
      }

      console.log(`✅ Added ${noteType} strategy notes to ${updates.length} essay slots`);
      return { updatedSlots: updates.length };

    } catch (error) {
      console.error('Error adding strategy note:', error);
      throw error;
    }
  }

  /**
   * Find similar slots that could reuse the same essay
   * Only includes finalized slots (not exploratory or rejected)
   */
  static async findReusableSlots(slotId, userId) {
    try {
      const { data: currentSlot } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('id', slotId)
        .single();

      if (!currentSlot) return [];

      // Get all other slots for the user (excluding rejected/exploratory if they're in choice groups)
      const { data: allSlots } = await supabase
        .from('essay_slots')
        .select('*, college_journey_colleges(name)')
        .eq('user_id', userId)
        .neq('id', slotId)
        .eq('status', 'empty');

      if (!allSlots) return [];

      // Filter out rejected slots and exploratory slots from choice groups
      const eligibleSlots = allSlots.filter(slot => {
        // Always exclude rejected slots
        if (slot.selection_status === 'rejected') return false;

        // If slot is in a choice group and not finalized, exclude it
        if (slot.prompt_group_id && slot.selection_status !== 'finalized') return false;

        return true;
      });

      // Score slots based on similarity
      const scoredSlots = eligibleSlots.map(slot => {
        let score = 0;

        // Same slot type = high score
        if (slot.slot_type === currentSlot.slot_type) score += 10;

        // Similar word limits
        if (Math.abs(slot.word_limit - currentSlot.word_limit) <= 50) score += 5;

        // Similar prompt keywords (basic text similarity)
        if (slot.prompt_text && currentSlot.prompt_text) {
          const currentWords = currentSlot.prompt_text.toLowerCase().split(/\s+/);
          const slotWords = slot.prompt_text.toLowerCase().split(/\s+/);
          const commonWords = currentWords.filter(word => 
            slotWords.includes(word) && word.length > 3
          );
          score += commonWords.length;
        }

        return { ...slot, similarity_score: score };
      });

      // Return slots with score > 5, sorted by score
      return scoredSlots
        .filter(slot => slot.similarity_score > 5)
        .sort((a, b) => b.similarity_score - a.similarity_score)
        .slice(0, 5); // Top 5 matches

    } catch (error) {
      console.error('Error finding reusable slots:', error);
      return [];
    }
  }

  /**
   * Mark slots as reusable
   */
  static async markSlotsAsReusable(sourceSlotId, targetSlotIds) {
    try {
      // Update source slot with reusable targets
      await supabase
        .from('essay_slots')
        .update({ can_reuse_for: targetSlotIds })
        .eq('id', sourceSlotId);

      // Update target slots to reference source
      for (const targetId of targetSlotIds) {
        await supabase
          .from('essay_slots')
          .update({ is_reused_from: sourceSlotId })
          .eq('id', targetId);
      }

      return true;
    } catch (error) {
      console.error('Error marking slots as reusable:', error);
      return false;
    }
  }

  /**
   * Get slot overview for a user
   */
  static async getUserSlotOverview(userId) {
    try {
      // First try to get from the view
      let { data, error } = await supabase
        .from('essay_slots_overview')
        .select('*')
        .eq('user_id', userId)
        .order('college_name', { ascending: true })
        .order('essay_slot', { ascending: true });

      // If view doesn't exist or returns error, fall back to direct query
      if (error) {
        console.log('essay_slots_overview view not available, using direct query:', error.message);
        
        // Query the essay_slots table directly with proper joins
        const slotsResult = await supabase
          .from('essay_slots')
          .select(`
            *,
            college_journey_colleges!college_id (
              name,
              category,
              global_college_id,
              global_colleges (
                ed_deadline,
                ea_deadline,
                rd_deadline
              )
            )
          `)
          .eq('user_id', userId)
          .order('college_id', { ascending: true })
          .order('essay_slot', { ascending: true });
        
        if (slotsResult.error) throw slotsResult.error;
        
        // Transform the data to match the expected format
        data = (slotsResult.data || []).map(slot => ({
          ...slot,
          college_name: slot.college_journey_colleges?.name || null,
          college_category: slot.college_journey_colleges?.category,
          early_decision_deadline: slot.college_journey_colleges?.global_colleges?.ed_deadline,
          early_action_deadline: slot.college_journey_colleges?.global_colleges?.ea_deadline,
          regular_decision_deadline: slot.college_journey_colleges?.global_colleges?.rd_deadline
        }));
      }

      if (!data) data = [];

      // Group by college for better organization
      const grouped = {};
      let personalStatement = null;

      data.forEach(slot => {
        if (slot.slot_type === 'personal_statement') {
          personalStatement = slot;
        } else {
          const collegeName = slot.college_name || 'Unknown College';
          if (!grouped[collegeName]) {
            grouped[collegeName] = {
              college_id: slot.college_id,
              college_name: collegeName,
              college_category: slot.college_category,
              deadlines: {
                ed: slot.early_decision_deadline,
                ea: slot.early_action_deadline,
                rd: slot.regular_decision_deadline
              },
              slots: []
            };
          }
          grouped[collegeName].slots.push(slot);
        }
      });

      return {
        personalStatement,
        colleges: Object.values(grouped),
        totalSlots: data.length,
        completedSlots: data.filter(s => s.status === 'complete').length,
        inProgressSlots: data.filter(s => 
          ['brainstorming', 'drafting', 'revising', 'review'].includes(s.status)
        ).length
      };

    } catch (error) {
      console.error('Error getting user slot overview:', error);
      throw error;
    }
  }

  // Helper methods

  static calculateSlotDeadline(college) {
    // Determine the application deadline based on the chosen application round
    const applicationRound = college.application_round || 'regular_decision';
    let applicationDeadline = null;
    
    // Get the appropriate deadline based on chosen round
    switch (applicationRound) {
      case 'early_decision':
      case 'early_decision_1':
        applicationDeadline = college.ed_deadline || college.global_colleges?.ed_deadline;
        break;
      case 'early_decision_2':
        applicationDeadline = college.ed2_deadline || college.global_colleges?.ed2_deadline;
        break;
      case 'early_action':
        applicationDeadline = college.ea_deadline || college.global_colleges?.ea_deadline;
        break;
      case 'regular_decision':
      default:
        applicationDeadline = college.rd_deadline || college.global_colleges?.rd_deadline;
        break;
    }
    
    // If no deadline for chosen round, fall back to earliest available
    if (!applicationDeadline) {
      const deadlines = [
        college.ed_deadline || college.global_colleges?.ed_deadline,
        college.ea_deadline || college.global_colleges?.ea_deadline,
        college.rd_deadline || college.global_colleges?.rd_deadline
      ].filter(Boolean).sort();
      
      applicationDeadline = deadlines[0];
    }
    
    if (!applicationDeadline) {
      // Default to January 1st if no deadlines
      return new Date(new Date().getFullYear() + 1, 0, 1).toISOString();
    }

    // Essay tasks are due the day BEFORE the application deadline
    const essayDeadline = new Date(applicationDeadline);
    essayDeadline.setDate(essayDeadline.getDate() - 1);
    return essayDeadline.toISOString();
  }

  static getSlotTypeLabel(slotType) {
    const labels = {
      personal_statement: 'Personal Statement',
      supplemental: 'Supplemental Essay',
      why_us: '"Why Us" Essay',
      why_major: '"Why Major" Essay',
      community: 'Community Essay',
      diversity: 'Diversity Essay',
      academic_interest: 'Academic Interest',
      extracurricular: 'Extracurricular Essay',
      scholarship: 'Scholarship Essay',
      honors_program: 'Honors Program Essay',
      short_answer: 'Short Answer',
      video_essay: 'Video Essay',
      portfolio: 'Portfolio',
      other: 'Additional Essay'
    };
    return labels[slotType] || slotType;
  }

  static mapSlotStatusToTimeline(slotStatus) {
    const mapping = {
      empty: 'not_started',
      brainstorming: 'ready',
      drafting: 'in_progress',
      revising: 'in_progress',
      review: 'pending',
      complete: 'complete'
    };
    return mapping[slotStatus] || 'not_started';
  }

  /**
   * Get estimated duration for essay tasks based on type and word limit
   */
  static getEssayEstimatedDuration(slot) {
    const wordLimit = slot.word_limit || 250;
    const slotType = slot.slot_type;
    
    // Base duration by essay type
    let baseDuration = 120; // Default 2 hours
    
    switch (slotType) {
      case 'personal_statement':
        baseDuration = 240; // 4 hours for personal statement
        break;
      case 'why_us':
      case 'why_major':
        baseDuration = 90; // 1.5 hours for focused essays
        break;
      case 'short_answer':
        baseDuration = 45; // 45 minutes for short answers
        break;
      case 'supplemental':
      default:
        baseDuration = 120; // 2 hours for general supplemental essays
    }
    
    // Adjust based on word limit
    if (wordLimit > 500) {
      baseDuration += 60; // Add 1 hour for longer essays
    } else if (wordLimit < 150) {
      baseDuration -= 30; // Subtract 30 mins for very short essays
    }
    
    return Math.max(30, baseDuration); // Minimum 30 minutes
  }

  /**
   * Get appropriate module link based on essay type
   */
  static getEssayModuleLink(slotType) {
    switch (slotType) {
      case 'personal_statement':
        return '/college/essays?tab=free-write';
      case 'why_us':
      case 'why_major':
      case 'supplemental':
        return '/college/essays?tab=essays';
      case 'short_answer':
        return '/college/essays?tab=ideas';
      default:
        return '/college/essays';
    }
  }

  /**
   * Get helpful resource URLs based on essay type
   */
  static getEssayResourceUrls(slotType) {
    const commonResources = [
      'https://www.commonapp.org/apply/essay-prompts',
      'https://www.collegeessayguy.com/'
    ];
    
    switch (slotType) {
      case 'personal_statement':
        return [
          ...commonResources,
          'https://www.collegeessayguy.com/personal-statement'
        ];
      case 'why_us':
        return [
          ...commonResources,
          'https://www.collegeessayguy.com/why-us-essay'
        ];
      case 'why_major':
        return [
          ...commonResources,
          'https://www.collegeessayguy.com/why-major-essay'
        ];
      default:
        return commonResources;
    }
  }
}

export default EssaySlotService;